import { useState } from 'react'
import { Link } from 'react-router-dom'
import { getApiEndpoint } from '../config/api'
import { useTheme } from '../contexts/ThemeContext'

const Contact = () => {
  const { isNight } = useTheme()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: '',
    smsConsent: false,
  })
  const [status, setStatus] = useState({ type: '', message: '' })
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value
    setFormData({
      ...formData,
      [e.target.name]: value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setStatus({ type: '', message: '' })

    try {
      const response = await fetch(getApiEndpoint('/contact'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      })

      const data = await response.json()

      if (data.success) {
        setStatus({ type: 'success', message: 'Thank you! Your message has been sent successfully.' })
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: '',
          message: '',
          smsConsent: false,
        })
      } else {
        setStatus({ type: 'error', message: data.message || 'Failed to send message. Please try again.' })
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'An error occurred. Please try again later.' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={isNight ? 'bg-void' : 'bg-gradient-to-b from-white to-gray-50'}>
      {/* Header */}
      <div className={`py-16 ${isNight ? 'bg-gradient-to-r from-void-light/80 to-void-elevated/80 backdrop-blur-sm' : 'glass-hero text-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${isNight ? 'font-orbitron text-cyber-cyan' : ''}`}>
            {isNight ? 'CONTACT US' : 'Contact Us'}
          </h1>
          <p className={isNight ? 'text-xl text-gray-400' : 'text-xl text-primary-100'}>
            {isNight ? "Let's discuss how we can help secure your organization" : "Let's discuss how we can help secure your organization"}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 className={`text-3xl font-bold mb-6 ${isNight ? 'text-white font-orbitron' : 'text-gray-900'}`}>
              {isNight ? 'GET IN TOUCH' : 'Get in Touch'}
            </h2>
            <p className={`mb-8 ${isNight ? 'text-gray-400' : 'text-gray-600'}`}>
              {isNight
                ? 'Have questions about our services? Fill out the form and we\'ll get back to you shortly.'
                : 'Have questions about our services? Want to schedule a consultation? Fill out the form and we\'ll get back to you shortly.'}
            </p>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className={`text-2xl mr-4 ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className={`font-semibold mb-1 ${isNight ? 'text-white' : 'text-gray-900'}`}>Email</h3>
                  <a href="mailto:info@vigilantedge.tech" className={`hover:underline ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>
                    info@vigilantedge.tech
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <div className={`text-2xl mr-4 ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className={`font-semibold mb-1 ${isNight ? 'text-white' : 'text-gray-900'}`}>
                    {isNight ? 'Schedule a Meeting' : 'Schedule a Meeting'}
                  </h3>
                  <p className={isNight ? 'text-gray-400' : 'text-gray-600'}>
                    Book a consultation directly on our calendar
                  </p>
                  <Link
                    to="/book-appointment"
                    className={`mt-2 inline-block hover:underline ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}
                  >
                    Book an Appointment →
                  </Link>
                </div>
              </div>

              <div className="flex items-start">
                <div className={`text-2xl mr-4 ${isNight ? 'text-cyber-cyan' : 'text-primary-600'}`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className={`font-semibold mb-1 ${isNight ? 'text-white' : 'text-gray-900'}`}>Response Time</h3>
                  <p className={isNight ? 'text-gray-400' : 'text-gray-600'}>
                    {isNight ? 'We typically respond within 24 hours during business days' : 'We typically respond within 24 hours during business days'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className={isNight ? 'neon-border-gradient p-6' : 'card'}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label htmlFor="company" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label htmlFor="phone" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  Phone
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="subject" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  Subject *
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                >
                  <option value="">Select a service</option>
                  <option value="External Network Penetration Test">External Network Penetration Test</option>
                  <option value="External Web Application Penetration Test">External Web Application Penetration Test</option>
                  <option value="External OSINT Assessment">External OSINT Assessment</option>
                  <option value="Internal Network Security Controls Assessment">Internal Network Security Controls Assessment</option>
                  <option value="Internal Vulnerability Assessment">Internal Vulnerability Assessment</option>
                  <option value="General Inquiry">General Inquiry</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className={`block text-sm font-medium mb-2 ${isNight ? 'text-gray-300' : 'text-gray-700'}`}>
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className={isNight ? 'glass-input-night' : 'glass-input'}
                  placeholder="Tell us about your security needs..."
                />
              </div>

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="smsConsent"
                    name="smsConsent"
                    type="checkbox"
                    checked={formData.smsConsent}
                    onChange={handleChange}
                    className={`h-4 w-4 rounded ${isNight ? 'bg-void-elevated border-void-elevated text-cyber-cyan focus:ring-cyber-cyan' : 'text-primary-600 focus:ring-primary-500 border-gray-300'}`}
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="smsConsent" className={isNight ? 'text-gray-300' : 'text-gray-700'}>
                    I consent to receive SMS notifications from VigilantEdge
                  </label>
                  <p className={`text-xs mt-1 ${isNight ? 'text-gray-500' : 'text-gray-500'}`}>
                    By checking this box, you agree to receive automated text messages about appointments, service updates, and security notifications. Message frequency varies. Message and data rates may apply. Reply STOP to opt out. View our{' '}
                    <Link to="/privacy-policy" className={isNight ? 'text-cyber-cyan hover:underline' : 'text-primary-600 hover:underline'}>Privacy Policy</Link>
                    {' '}and{' '}
                    <Link to="/terms-of-service" className={isNight ? 'text-cyber-cyan hover:underline' : 'text-primary-600 hover:underline'}>Terms of Service</Link>.
                  </p>
                </div>
              </div>

              {status.message && (
                <div
                  className={`p-4 rounded-lg backdrop-blur-sm ${
                    status.type === 'success'
                      ? isNight ? 'bg-green-900/40 text-green-400 border border-green-500/30' : 'bg-green-50/80 text-green-800 border border-green-200/50'
                      : isNight ? 'bg-red-900/40 text-red-400 border border-red-500/30' : 'bg-red-50/80 text-red-800 border border-red-200/50'
                  }`}
                >
                  {status.message}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full disabled:opacity-50 disabled:cursor-not-allowed ${isNight ? 'btn-neon' : 'btn-primary'}`}
              >
                {loading ? 'Sending...' : isNight ? 'Send Message' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Contact
